import renderingInServer from '@shared/renderingInServer';
import { connect, ConnectedProps } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isNull } from 'lodash';
import { hasCurrentUser, hasCurrentUserLoaded } from '@store/selectors';
import ListingAgentsContactCard from '@components/shared/ListingAgentsContactCard';
import ContactCardCTA from '@components/shared/ContactCardCTA';
import { AgentContact, StoreState } from '@root/types';
import useQueryParams from '@shared/useQueryParams';
import { getSessionStorageValues } from '@shared/sessionStorageForSiteVisitors';
import InquiryForm from '@components/layouts/Truva/Listing/InquiryForm';

type Props = {
  emailSubjectName: string;
  hideLocation?: boolean;
  isPreview?: boolean;
  listingAgents: AgentContact[];
  listingId?: string;
  skipInquiry?: boolean;
  title: string;
  trackContactClick: () => void;
};

const mapState = (state: StoreState) => ({
  userIsLoggedIn: hasCurrentUser(state),
  userHasLoaded: hasCurrentUserLoaded(state) as boolean,
  currentUser: state.currentUser,
});

const connector = connect(mapState);
type ReduxProps = ConnectedProps<typeof connector>;

// This component helps us render variations
// of contact cards in both listing and building pages
// based on factors like user being signed in,
// user got to the listing or building page
// after following a share link etc.
export const RawContactChooser = ({
  currentUser,
  emailSubjectName,
  hideLocation = false,
  isPreview = false,
  listingAgents,
  listingId,
  skipInquiry,
  title,
  trackContactClick,
  userHasLoaded,
  userIsLoggedIn,
}: Props & ReduxProps) => {
  const flags = useFlags();
  const { hc } = useQueryParams();
  if (!userHasLoaded || hc === 'h1d3' || renderingInServer()) return null;

  // not a hook
  const { sessionStorageListingAgents, arrivedOnMarketLink } = getSessionStorageValues();

  if (userIsLoggedIn || isPreview) {
    if (skipInquiry) {
      return (
        <ListingAgentsContactCard
          listingAgents={sessionStorageListingAgents || listingAgents}
          emailSubjectName={emailSubjectName}
          title={title}
          trackContactClick={trackContactClick}
        />
      );
    }

    return (
      <InquiryForm
        currentUser={currentUser}
        listingAgents={listingAgents}
        listingAddress={emailSubjectName}
        listingId={listingId!}
        hideLocation={hideLocation}
      />
    );
  }

  if (
    flags['sign-up-cta-in-contact-card'] &&
    !arrivedOnMarketLink &&
    isNull(sessionStorageListingAgents) &&
    !isPreview
  ) {
    return <ContactCardCTA source={listingId ? 'listing page' : 'building page'} />;
  }

  return (
    <ListingAgentsContactCard
      listingAgents={sessionStorageListingAgents || listingAgents}
      emailSubjectName={emailSubjectName}
      title={title}
      trackContactClick={trackContactClick}
    />
  );
};

export default connector(RawContactChooser);
